import * as React from 'react';
import { Bike, Plus, Trash2 } from 'lucide-react';
import {
  CYCLE_TYPE_LABELS,
  CYCLE_TYPES,
  createBicycleSchema,
  type Bicycle,
  type CycleType,
} from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/primitives';
import { EmptyState, LoadingState, PageHeader } from '~/components/shared/states';
import { ConfirmDialog } from '~/components/shared/confirm-dialog';
import { api } from '~/lib/api';
import { queryKeys, useBicycles, useCrudMutation } from '~/lib/queries';

export function ClientBicyclesPage() {
  const { data, isLoading } = useBicycles();
  const bicycles = data ?? [];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<Bicycle | null>(null);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = React.useState({
    brand: '',
    model: '',
    manufacturedYear: currentYear,
    type: 'BIKE' as CycleType,
    serialNumber: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const create = useCrudMutation({
    request: (input: Record<string, unknown>) => api.post('/me/bicycles', input),
    invalidate: [queryKeys.bicycles],
    successMessage: 'Cycle ajoute.',
  });

  const remove = useCrudMutation({
    request: (id: string) => api.delete(`/me/bicycles/${id}`),
    invalidate: [queryKeys.bicycles],
    successMessage: 'Cycle supprime.',
  });

  async function submit() {
    setErrors({});
    const parsed = createBicycleSchema.safeParse({
      ...form,
      serialNumber: form.serialNumber || null,
    });

    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join('.'), issue.message]),
        ),
      );
      return;
    }

    await create.mutateAsync(parsed.data as Record<string, unknown>);
    setDialogOpen(false);
    setForm({
      brand: '',
      model: '',
      manufacturedYear: currentYear,
      type: 'BIKE',
      serialNumber: '',
    });
  }

  return (
    <div className="app-page">
      <PageHeader
        title="Mes cycles"
        description="Enregistrez vos velos pour aller plus vite lors de vos prochaines reservations."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus aria-hidden />
            Ajouter un cycle
          </Button>
        }
      />

      {isLoading ? <LoadingState /> : null}

      {!isLoading && bicycles.length === 0 ? (
        <EmptyState
          icon={Bike}
          title="Aucun cycle enregistre"
          description="Ajoutez votre velo pour ne plus avoir a saisir ses caracteristiques."
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {bicycles.map((bicycle) => (
          <Card key={bicycle.id} className="gap-3 py-4">
            <CardContent className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p className="truncate font-medium">
                  {bicycle.brand} {bicycle.model}
                </p>
                <p className="text-xs text-muted-foreground">{bicycle.manufacturedYear}</p>
                <Badge variant="secondary">{CYCLE_TYPE_LABELS[bicycle.type]}</Badge>
                {bicycle.serialNumber ? (
                  <p className="text-xs text-muted-foreground">N° {bicycle.serialNumber}</p>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setToDelete(bicycle)}
                aria-label={`Supprimer ${bicycle.brand} ${bicycle.model}`}
              >
                <Trash2 className="text-destructive" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un cycle</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Marque" htmlFor="brand" error={errors.brand} required>
              <Input
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </Field>
            <Field label="Modele" htmlFor="model" error={errors.model} required>
              <Input
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </Field>
            <Field label="Annee" htmlFor="year" error={errors.manufacturedYear} required>
              <Input
                type="number"
                min={1950}
                max={currentYear + 1}
                value={form.manufacturedYear}
                onChange={(e) => setForm({ ...form, manufacturedYear: Number(e.target.value) })}
              />
            </Field>
            <Field label="Type" htmlFor="type" required>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v as CycleType })}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CYCLE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {CYCLE_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Numero de serie"
              htmlFor="serial"
              hint="Facultatif"
              className="sm:col-span-2"
            >
              <Input
                value={form.serialNumber}
                onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
              />
            </Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button loading={create.isPending} onClick={() => void submit()}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Supprimer ce cycle ?"
        description="Vos interventions passees le concernant restent consultables."
        variant="destructive"
        confirmLabel="Supprimer"
        loading={remove.isPending}
        onConfirm={async () => {
          if (!toDelete) return;
          await remove.mutateAsync(toDelete.id);
          setToDelete(null);
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Mes adresses
// ═══════════════════════════════════════════════════════════════════════════════
